import React, { useState, useEffect, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { db } from "../../../../firebase";
import { ref, onValue, update, remove, get, set } from "firebase/database";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Grid,
  OutlinedInput,
  InputAdornment,
  IconButton,
  useTheme,
  Stack,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Formik } from "formik";
import * as yup from "yup";

import { getDriverAndVehicle } from "../../../../api/users/driver/driver";
import {
  getTokenRefresh,
  verifyExpireTokenRefresh,
} from "../../../../helpers/token/refreshToken";
import SnackBar from "../../../../components/snackbar/SnackBar";
import {
  createBookingNow,
  updateDataBookingNow,
} from "../../../../api/booking/bookingNow";
import { tokens } from "../../../../theme";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { suggestionBackgroundColor } from "../booking/style/BookingStyle";
import { sendBookingDriverNotificationUseCase } from "../../../../features/booking/driverNotification/domain/usecases/sendBookingDriverNotificationUseCase";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { formatterTimePicker } from "../../../../helpers/date/formatterTimePicker";
import { dateFormat, timeFormat } from "../../../../helpers/date/date";
import { UpdateOrderBackup } from "../backup/usecases/UpdateOrderBackup";
import { DuplicateOrderBackup } from "../backup/usecases/DuplicateOrderBackup";
import { CreateReturnOrderBackup } from "../backup/usecases/CreateReturnOrderBackup";
import { createBookingCopy } from "../../../../helpers/booking/createBookingCopy";
import {
  createAdvanceCash,
  getAdvanceCashOrder,
} from "../../../../api/booking/advance";
import { EndDateTimeDialog } from "../booking/components/EndDateTimeDialog";
import {
  formatDatePickerValue,
  formatTimePickerValue,
  getDateTimeValue,
  getDateOnlyValue,
} from "../booking/components/endDateTimeUtils";
import "../booking/style/booking.css";

function getOrderedSubRoutes(booking) {
  const subRoutes = booking?.subRoutes ? Object.values(booking.subRoutes) : [];
  const destination = booking?.destination;
  const orderedSubRoutes = subRoutes
    .filter((subRoute) => subRoute?.name)
    .filter(
      (subRoute) =>
        subRoute.name !== destination?.name ||
        subRoute.lat !== destination?.lat ||
        subRoute.lng !== destination?.lng,
    )
    .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));

  if (destination?.name) {
    orderedSubRoutes.push(destination);
  }

  return orderedSubRoutes;
}

function getDestinationValue(subRoutes) {
  if (!Array.isArray(subRoutes) || subRoutes.length === 0) return "";
  if (subRoutes.length === 1) return subRoutes[0].name || "";

  return `${subRoutes.length} paradas`;
}

function arrayMove(items, fromIndex, toIndex) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);

  return nextItems;
}

function getSortableSubRoutes(subRoutes) {
  return subRoutes.map((subRoute, index) => ({
    ...subRoute,
    sortableId: `booking-later-sub-route-${subRoute.id || "destination"}-${index}`,
  }));
}

function getSubRoutesFromModalList(subRoutes) {
  return subRoutes.map(({ sortableId, ...subRoute }) => subRoute);
}

function getRouteDestination(route) {
  if (!route) return null;

  const { sortableId, id, ...destination } = route;
  return destination;
}

function getBookingRouteValues(subRoutes) {
  const routes = Array.isArray(subRoutes)
    ? subRoutes
        .map((route) => {
          if (!route) return null;

          const { sortableId, ...subRoute } = route;
          return subRoute;
        })
        .filter((route) => route?.name)
    : [];

  if (routes.length === 0) {
    return {
      destination: null,
      subRoutes: [],
    };
  }

  return {
    destination: getRouteDestination(routes[routes.length - 1]),
    subRoutes: routes.slice(0, -1).map((route, index) => ({
      ...route,
      id: index + 1,
    })),
  };
}

function getNextSubRouteId(subRoutes) {
  const ids = subRoutes.map((subRoute) => Number(subRoute.id || 0));
  return Math.max(0, ...ids) + 1;
}

function SubRouteSortableItem({ subRoute, index, onDelete }) {
  const { ref, handleRef, isDragging } = useSortable({
    id: String(subRoute.sortableId),
    index,
    group: "booking-later-sub-routes",
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        py: 0.75,
        border: "1px solid rgba(20, 83, 45, 0.16)",
        borderRadius: 1,
        bgcolor: isDragging ? "rgba(20, 83, 45, 0.08)" : "background.paper",
      }}
    >
      <IconButton ref={handleRef} size="small" sx={{ cursor: "grab" }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {index + 1}. {subRoute.name}
      </Typography>
      <IconButton
        size="small"
        color="error"
        aria-label="eliminar parada"
        onClick={() => onDelete(subRoute.sortableId)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const FormBookingLater = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id, uuid } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [driver, setDriver] = useState(null);
  const { t } = useTranslation();
  const [discount, setDiscount] = useState(0);
  const [advance, setAdvance] = useState(0);
  const [noteAdmin, setNoteAdmin] = useState("");
  const [datePay, setDatePay] = useState("");

  const [commissionOption, setCommissionOption] = useState(true);
  const [commissionValue, setCommissionValue] = useState(0);

  const [activePhone, setActivePhone] = useState(true);
  const handleActivePhone = (event) => setActivePhone(event.target.checked);
  const [otherPersonDialogOpen, setOtherPersonDialogOpen] = useState(false);
  const [copyDialogType, setCopyDialogType] = useState(null);
  const [copyDate, setCopyDate] = useState(null);
  const [copyTime, setCopyTime] = useState(null);
  const [openSubRoutesModal, setOpenSubRoutesModal] = useState(false);
  const [subRoutesModalList, setSubRoutesModalList] = useState([]);
  const [subRouteSearch, setSubRouteSearch] = useState("");
  const [otherPersonForm, setOtherPersonForm] = useState({
    otherPersonName: "",
    otherPersonPhone: "",
  });

  const handleOpenOtherPersonDialog = (values) => {
    setOtherPersonForm({
      otherPersonName: values.otherPersonName ?? "",
      otherPersonPhone: values.otherPersonPhone ?? "",
    });
    setOtherPersonDialogOpen(true);
  };

  const handleCloseOtherPersonDialog = () => {
    setOtherPersonDialogOpen(false);
  };

  const handleOpenCopyDialog = (type) => {
    setCopyDate(getDateOnlyValue(booking?.date));
    setCopyTime(getDateTimeValue(booking?.date, booking?.time));
    setCopyDialogType(type);
  };

  const handleCloseCopyDialog = () => setCopyDialogType(null);

  const handleAcceptCopyDialog = async () => {
    if (!booking?.counterBooking) {
      snackBarDialog("No se encontro el pedido", "error");
      return;
    }

    const selectedDate = dayjs(copyDate);
    const selectedTime = dayjs(copyTime);
    if (
      !copyDate ||
      !copyTime ||
      !selectedDate.isValid() ||
      !selectedTime.isValid()
    ) {
      snackBarDialog("Debe ingresar una fecha y hora validas", "error");
      return;
    }

    const date = selectedDate.format("DD/MM/YYYY");
    const time = selectedTime.format("hh:mm A");

    setIsLoading(true);
    try {
      const originalSnapshot = await get(ref(db, `bookingNow/${uuid}`));
      if (!originalSnapshot.exists()) {
        snackBarDialog("ESTE PEDIDO YA NO EXISTE", "error");
        return;
      }

      const originalBooking = originalSnapshot.val();
      const isReturn = copyDialogType === "return";
      const response = isReturn
        ? await CreateReturnOrderBackup({
            id: Number(originalBooking.counterBooking),
            date,
            time,
          })
        : await DuplicateOrderBackup({
            id: originalBooking.counterBooking,
            date,
            time,
          });
      const newCounterBooking = isReturn ? response?.data?.id : response?.id;
      const newUuid = isReturn ? response?.data?.uuid : response?.uuid;

      if (!newCounterBooking || !newUuid) {
        snackBarDialog("No se logro crear el respaldo del pedido", "error");
        return;
      }

      const newBooking = createBookingCopy(originalBooking, {
        uuid: newUuid,
        counterBooking: newCounterBooking,
        date,
        time,
        isReturn,
      });
      await set(ref(db, `bookingNow/${newUuid}`), newBooking);
      setCopyDialogType(null);
      snackBarDialog(
        isReturn ? "PEDIDO DE RETORNO CREADO" : "PEDIDO DUPLICADO",
      );
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: No se pudo crear el pedido", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtherPersonChange = (event) => {
    const { name, value } = event.target;
    setOtherPersonForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleAcceptOtherPerson = (setFieldValue) => {
    setFieldValue("otherPersonName", otherPersonForm.otherPersonName);
    setFieldValue("otherPersonPhone", otherPersonForm.otherPersonPhone);
    setOtherPersonDialogOpen(false);
  };

  const handleClearOtherPerson = (setFieldValue) => {
    setOtherPersonForm({
      otherPersonName: "",
      otherPersonPhone: "",
    });
    setFieldValue("otherPersonName", "");
    setFieldValue("otherPersonPhone", "");
  };

  const hasOtherPersonData = (values) => {
    return Boolean(
      String(values.otherPersonName ?? "").trim() ||
      String(values.otherPersonPhone ?? "").trim(),
    );
  };

  const [otherPay, setOtherPay] = useState(false);
  const handleOtherPay = (e) => {
    const isChecked = e.target.checked;
    setOtherPay(isChecked);
    if (isChecked) {
      const currentDate = new Date();
      setDatePay(`${dateFormat(currentDate)} ${timeFormat(currentDate)}`);
    } else {
      setDatePay("");
    }
  };

  useEffect(() => {
    const starCountRef = ref(db, `bookingNow/${uuid}`);
    onValue(starCountRef, (snapshot) => {
      let data = snapshot.val();
      if (data === null) return console.log("No Booking");

      setAddressOrigin(data.origin.name);
      setCoordinatesOrigin({ lat: data.origin.lat, lng: data.origin.lng });
      setAddressDestination(data.destination.name);
      setCoordinatesDestination({
        lat: data.destination.lat,
        lng: data.destination.lng,
      });
      setBooking({
        ...data,
        subRoutes: getOrderedSubRoutes(data),
      });
    });
  }, []);

  const [dateformatter, setDateFormat] = useState("");
  const [timeFormatter, setTimeFormat] = useState("");
  const [dateEndFormat, setDateEndFormat] = useState("");
  const [timeEndFormatter, setTimeEndFormat] = useState("");
  const [endDateTimeDialogOpen, setEndDateTimeDialogOpen] = useState(false);
  const [listPayments, setListPayments] = useState([]);

  useEffect(() => {
    if (booking) {
      if (booking.driver) {
        const driver = {
          brand: booking.driver.brand,
          color: booking.driver.color,
          driver_id: booking.driver.driver_id,
          lastname: booking.driver.lastname,
          model: booking.driver.model,
          name: booking.driver.name,
          bodywork: booking.driver.bodywork,
          plate: booking.driver.plate,
          vehicle_id: booking.driver.vehicle_id,
          percentageValue: booking.driver.percentageValue,
        };
        setCommissionValue(driver.percentageValue);
        setDriver(driver);
      }
      setActivePhone(Boolean(booking?.phoneActive));
      setOtherPay(booking.otherPay ? booking.otherPay : false);
      setDateFormat(dayjs(booking.date, "DD/MM/YYYY").toDate());
      setTimeFormat(formatterTimePicker(booking.date, booking.time));
      if (booking.booking_type == 3) {
        setDateEndFormat(dayjs(booking.dateEnd, "DD/MM/YYYY").toDate());
        setTimeEndFormat(formatterTimePicker(booking.dateEnd, booking.timeEnd));
      }
      setDiscount(booking.discount ? booking.discount : 0);
      setAdvance(booking.advance ? booking.advance : 0);
      setNoteAdmin(
        `${booking.noteAdmin ?? ""} ${booking.otherPersonName ?? ""} ${booking.otherPersonPhone ?? ""}`,
      );
      const change =
        booking?.openpay?.operation_date != undefined &&
        booking?.openpay?.operation_date != "";
      setDatePay(change ? booking.openpay.operation_date : "");
      //traer los avances de firebase
      const getPaymentsMysql = async () => {
        try {
          const resp = await getPaymentsList(booking.counterBooking);
          console.log(resp.data);
          setListPayments(resp.data.resp);
        } catch (error) {
          console.log(error);
        }
      };
      getPaymentsMysql();
    }
  }, [booking]);

  const getPaymentsList = async (orderNumber) => {
    const resp = await getAdvanceCashOrder(orderNumber);
    return resp;
  };

  useEffect(() => {
    const driversData = async () => {
      const verify = verifyExpireTokenRefresh();
      if (verify) return console.log("no token");

      const token = getTokenRefresh();
      try {
        const response = await getDriverAndVehicle(token);
        console.log(response.data);
        setDrivers(response?.data);
      } catch (error) {
        console.log(error);
      }
    };
    driversData();
  }, []);

  const checkoutSchema = yup.object().shape({});

  const handleFormSubmit = async (values) => {
    setIsLoading(true);
    const routeValues = getBookingRouteValues(values.subRoutes);

    if (routeValues.destination) {
      values.destination = routeValues.destination;
      values.subRoutes = routeValues.subRoutes;
      setAddressDestination(routeValues.destination.name);
      setCoordinatesDestination({
        lat: routeValues.destination.lat,
        lng: routeValues.destination.lng,
      });
    }

    values.driver = driver;
    values.statusService = driver ? "ASIGNADO" : "PENDIENTE";
    values.phoneActive = activePhone;
    try {
      const urlRef = `bookingNow/${values.uuid}`;

      values.origin.name = addressOrigin;
      values.origin.lat = coordinatesOrigin.lat;
      values.origin.lng = coordinatesOrigin.lng;
      if (!routeValues.destination) {
        values.destination.name = addressDestination;
        values.destination.lat = coordinatesDestination.lat;
        values.destination.lng = coordinatesDestination.lng;
      }
      values.date = formatDatePickerValue(values.date);
      values.time = formatTimePickerValue(values.time);
      if (values.booking_type == 3) {
        values.dateEnd = formatDatePickerValue(values.dateEnd);
        values.timeEnd = formatTimePickerValue(values.timeEnd);
      } else {
        values.dateEnd = "";
        values.timeEnd = "";
      }

      values.advance = otherPay ? 0 : Number(advance);
      values.discount = discount === "" ? 0 : Number(discount);
      values.noteAdmin = noteAdmin;

      const openpay = {
        createBy: "A",
        status: "pending",
        btnPay: true,
        currency: "",
        transaction_id: "",
        order_id: "",
        operation_date: "",
        url: "",
      };

      values.openpay = openpay;

      if (driver) {
        const token = "asdf";
        if (!commissionOption) values.commission = commissionValue;
        else values.commission = values.driver.percentageValue;

        if (!values.isCreated) {
          values.isCreated = true;
          values.statusBooking = "PAGADO";
          values.assigned = true;

          console.log(values);

          await createBookingNow(token, values);

          const dbRef = ref(db, urlRef);
          await remove(dbRef);

          const notification = await sendBookingDriverNotificationUseCase(values);
          if (!notification.success) {
            snackBarDialog(notification.error, "warning");
          } else if (notification.data?.successCount > 0) {
            snackBarDialog("CONDUCTOR ASIGNADO Y NOTIFICACION ENVIADA");
          } else if (notification.data?.failureCount > 0) {
            snackBarDialog(
              "CONDUCTOR ASIGNADO, PERO NO SE PUDO ENVIAR LA NOTIFICACION",
              "warning",
            );
          } else {
            snackBarDialog(
              "CONDUCTOR ASIGNADO, PERO NO TIENE TOKEN PARA RECIBIR NOTIFICACIONES",
              "warning",
            );
          }
        } else {
          await updateDataBookingNow(token, values);
        }

        setTimeout(() => {
          navigate("/admin/list-book-later");
        }, 5000);
      } else {
        const backup = {
          id: values.counterBooking,
          originName: addressOrigin,
          originLat: coordinatesOrigin.lat,
          originLng: coordinatesOrigin.lng,
          destinationName: values.destination.name,
          destinationLat: values.destination.lat,
          destinationLng: values.destination.lng,
          subRoutes: values.subRoutes,
          date: values.date,
          time: values.time,
          dateEnd: values.booking_type == 3 ? values.dateEnd : "",
          timeEnd: values.booking_type == 3 ? values.timeEnd : "",
          bookingType: values.booking_type,
          persons: values.persons,
          advance: advance,
          tax: values.tax,
          toll: values.toll,
          price: values.price,
          total: values.total,
          discount: discount,
          note: values.note,
          noteAdmin: noteAdmin,
          currency: values.currency,
          paymentType: values.payment_type,
          otherPay: otherPay,
          operationDate: datePay,
          otherPersonName: values.otherPersonName ?? "",
          otherPersonPhone: values.otherPersonPhone ?? "",
        };

        await update(ref(db, urlRef), values); //actualiza firebase

        if (otherPay) {
          const currencyList = {
            1: "PEN",
            2: "USD",
          };
          const advReg = {
            orderNumber: values.counterBooking,
            transactionId: `${values.counterBooking}-${Date.now()}`,
            currency: currencyList[values.currency],
            amount: advance,
            operationDate: datePay,
            status: "completed",
            createdBy: "ADMIN",
          };
          const resp = await createAdvanceCash(advReg);
          console.log(resp.data);
        }

        const putBackup = await UpdateOrderBackup(backup);

        if (putBackup.message === "success") {
          snackBarDialog("BACKUP ACTUALIZADO");
          setTimeout(() => {
            navigate("/admin/list-book-later");
          }, 5000);
        } else {
          snackBarDialog("Ha ocurrido un error al actualizar el backup");
        }
      }

      setIsLoading(false);
      // snackBarDialog("Pedido Actualizado");
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      snackBarDialog("Error: No Actualizado", "error");
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const handleSnkClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const handleMouseDownPassword = (e) => e.preventDefault();
  //select origin
  const [addressOrigin, setAddressOrigin] = useState("");
  const [addressDestination, setAddressDestination] = useState("");
  const [coordinatesOrigin, setCoordinatesOrigin] = useState({
    lat: null,
    lng: null,
  });
  const [coordinatesDestination, setCoordinatesDestination] = useState({
    lat: null,
    lng: null,
  });
  const handSelectOrigin = async (value) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    setAddressOrigin(value);
    setCoordinatesOrigin(ll);
  };

  const handSelectDestination = async (value, setFieldValue) => {
    const result = await geocodeByAddress(value);
    const ll = await getLatLng(result[0]);
    const destinationValue = {
      name: value,
      lat: ll.lat,
      lng: ll.lng,
    };

    setAddressDestination(value);
    setCoordinatesDestination(ll);
    setFieldValue("destination", destinationValue);
    setFieldValue("subRoutes", [destinationValue]);
    setBooking((currentBooking) => ({
      ...currentBooking,
      destination: destinationValue,
      subRoutes: [destinationValue],
    }));
  };
  const handleCloseEndDateTimeDialog = () => {
    setEndDateTimeDialogOpen(false);
  };
  const handleCloseSubRoutesModal = () => setOpenSubRoutesModal(false);
  const handleOpenSubRoutesModal = (subRoutes) => {
    const currentSubRoutes =
      subRoutes?.length > 0
        ? subRoutes
        : [
            {
              name: addressDestination,
              lat: coordinatesDestination.lat,
              lng: coordinatesDestination.lng,
            },
          ];

    setSubRoutesModalList(getSortableSubRoutes(currentSubRoutes));
    setSubRouteSearch("");
    setOpenSubRoutesModal(true);
  };
  const handleSubRoutesDragEnd = (event) => {
    const operation = event.operation;

    if (!operation || operation.canceled || !isSortableOperation(operation)) {
      return;
    }

    const sourceIndex = operation.source.initialIndex;
    const targetIndex = operation.source.index;
    if (sourceIndex === targetIndex) return;

    setSubRoutesModalList((currentList) =>
      arrayMove(currentList, sourceIndex, targetIndex),
    );
  };
  const handleDeleteSubRoute = (sortableId) => {
    setSubRoutesModalList((currentList) =>
      currentList.filter((subRoute) => subRoute.sortableId !== sortableId),
    );
  };
  const handleAddSubRoute = async (value) => {
    try {
      const result = await geocodeByAddress(value);
      const ll = await getLatLng(result[0]);

      setSubRoutesModalList((currentList) => {
        const id = getNextSubRouteId(currentList);
        const newSubRoute = {
          id,
          name: value,
          lat: ll.lat,
          lng: ll.lng,
          sortableId: `booking-later-sub-route-${id}-${Date.now()}`,
        };

        return [...currentList, newSubRoute];
      });
      setSubRouteSearch("");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE AGREGO LA PARADA", "error");
    }
  };

  return (
    <div>
      {booking && (
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={booking}
          validationSchema={checkoutSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => {
            const handleOpenEndDateTimeDialog = () => {
              if (!dateEndFormat && values.dateEnd) {
                setDateEndFormat(values.dateEnd);
              } else if (!dateEndFormat && dateformatter) {
                setDateEndFormat(dateformatter);
                setFieldValue("dateEnd", dateformatter);
              }

              if (!timeEndFormatter && values.timeEnd) {
                setTimeEndFormat(values.timeEnd);
              } else if (!timeEndFormatter && timeFormatter) {
                setTimeEndFormat(timeFormatter);
                setFieldValue("timeEnd", timeFormatter);
              }

              setEndDateTimeDialogOpen(true);
            };

            const handleAcceptEndDateTimeDialog = () => {
              if (!dateEndFormat || !timeEndFormatter) {
                snackBarDialog("Debe ingresar fecha y hora final", "error");
                return;
              }

              const startDate = getDateOnlyValue(values.date || dateformatter);
              const endDate = getDateOnlyValue(dateEndFormat);
              const startDateTime = getDateTimeValue(
                values.date || dateformatter,
                values.time || timeFormatter,
              );
              const endDateTime = getDateTimeValue(
                dateEndFormat,
                timeEndFormatter,
              );

              if (!startDate || !endDate) {
                snackBarDialog("Fecha final invalida", "error");
                return;
              }

              if (endDate < startDate) {
                snackBarDialog(
                  "La fecha final debe ser igual o superior a la fecha inicial",
                  "error",
                );
                return;
              }

              if (!startDateTime || !endDateTime) {
                snackBarDialog("Hora final invalida", "error");
                return;
              }

              if (endDateTime <= startDateTime) {
                snackBarDialog(
                  "La hora final debe ser superior a la hora inicial",
                  "error",
                );
                return;
              }

              setFieldValue("booking_type", 3);
              setFieldValue("dateEnd", dateEndFormat);
              setFieldValue("timeEnd", timeEndFormatter);
              setEndDateTimeDialogOpen(false);
            };

            const handleClearEndDateTimeDialog = () => {
              const previousBookingType =
                booking?.booking_type && booking.booking_type != 3
                  ? booking.booking_type
                  : 2;

              setDateEndFormat("");
              setTimeEndFormat("");
              setFieldValue("booking_type", previousBookingType);
              setFieldValue("dateEnd", "");
              setFieldValue("timeEnd", "");
              setEndDateTimeDialogOpen(false);
            };

            const handleSaveSubRoutesModal = () => {
              const subRoutes = getSubRoutesFromModalList(subRoutesModalList);
              const routeValues = getBookingRouteValues(subRoutes);

              if (!routeValues.destination) {
                snackBarDialog("Debe seleccionar un destino", "error");
                return;
              }

              setFieldValue("destination", routeValues.destination);
              setFieldValue("subRoutes", subRoutes);
              setBooking((currentBooking) => ({
                ...currentBooking,
                destination: routeValues.destination,
                subRoutes,
              }));
              setAddressDestination(routeValues.destination.name);
              setCoordinatesDestination({
                lat: routeValues.destination.lat,
                lng: routeValues.destination.lng,
              });
              handleCloseSubRoutesModal();
            };
            const hasSubRoutes = (values.subRoutes || []).length > 1;

            return (
              <form onSubmit={handleSubmit}>
                <Box backgroundColor={colors.primary[400]} p={1}>
                  <Grid container spacing={1} p={1}>
                    <Grid item xs={12} sm={6}>
                      <PlacesAutocomplete
                        value={addressOrigin}
                        onChange={setAddressOrigin}
                        onSelect={handSelectOrigin}
                      >
                        {({
                          getInputProps,
                          suggestions,
                          getSuggestionItemProps,
                          loading,
                        }) => (
                          <div key={suggestions.description}>
                            <FormControl fullWidth variant="outlined">
                              <OutlinedInput
                                id="outlined-adornment-weight"
                                onChange={handleChange("weight")}
                                aria-describedby="outlined-weight-helper-text"
                                {...getInputProps({
                                  placeholder: "Origen",
                                  className: "location-search-input",
                                })}
                              />
                            </FormControl>
                            <Box
                              key={suggestions.description}
                              className="autocomplete-dropdown-container suggestions"
                            >
                              {loading && <div>Loading...</div>}
                              {suggestions.map((suggestion) => {
                                const className = suggestion.active
                                  ? "suggestion-item--active"
                                  : "suggestion-item";
                                const style = suggestion.active
                                  ? suggestionBackgroundColor(
                                      colors.primary[400],
                                    )
                                  : suggestionBackgroundColor(
                                      colors.primary[400],
                                    );
                                return (
                                  <div
                                    key={suggestion.description}
                                    {...getSuggestionItemProps(suggestion, {
                                      className,
                                      style,
                                    })}
                                  >
                                    <span>{suggestion.description}</span>
                                  </div>
                                );
                              })}
                            </Box>
                          </div>
                        )}
                      </PlacesAutocomplete>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PlacesAutocomplete
                        value={
                          hasSubRoutes
                            ? getDestinationValue(values.subRoutes)
                            : addressDestination
                        }
                        onChange={(value) => {
                          if (!hasSubRoutes) setAddressDestination(value);
                        }}
                        onSelect={(value) =>
                          handSelectDestination(value, setFieldValue)
                        }
                      >
                        {({
                          getInputProps,
                          suggestions,
                          getSuggestionItemProps,
                          loading,
                        }) => (
                          <div>
                            <FormControl fullWidth variant="outlined">
                              <OutlinedInput
                                id="outlined-adornment-weight"
                                onChange={handleChange("weight")}
                                aria-describedby="outlined-weight-helper-text"
                                {...getInputProps({
                                  placeholder: "Destino",
                                  className: "location-search-input",
                                  readOnly: hasSubRoutes,
                                  onClick: hasSubRoutes
                                    ? () =>
                                        handleOpenSubRoutesModal(
                                          values.subRoutes,
                                        )
                                    : undefined,
                                })}
                                endAdornment={
                                  <InputAdornment position="end">
                                    <IconButton
                                      edge="end"
                                      aria-label="rutas de destino"
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                      }}
                                      onClick={() =>
                                        handleOpenSubRoutesModal(
                                          values.subRoutes,
                                        )
                                      }
                                    >
                                      <LocationOnIcon />
                                    </IconButton>
                                  </InputAdornment>
                                }
                                sx={{
                                  "& .MuiInputBase-input": {
                                    cursor: hasSubRoutes ? "pointer" : "text",
                                  },
                                }}
                              />
                            </FormControl>
                            {!hasSubRoutes && (
                              <Box
                                key={suggestions.description}
                                className="autocomplete-dropdown-container suggestionsDestination"
                              >
                                {loading && <div>Loading...</div>}
                                {suggestions.map((suggestion) => {
                                  const className = suggestion.active
                                    ? "suggestion-item--active"
                                    : "suggestion-item";
                                  const style = suggestion.active
                                    ? suggestionBackgroundColor(
                                        colors.primary[400],
                                      )
                                    : suggestionBackgroundColor(
                                        colors.primary[400],
                                      );
                                  return (
                                    <div
                                      key={suggestion.description}
                                      {...getSuggestionItemProps(suggestion, {
                                        className,
                                        style,
                                      })}
                                    >
                                      <span>{suggestion.description}</span>
                                    </div>
                                  );
                                })}
                              </Box>
                            )}
                          </div>
                        )}
                      </PlacesAutocomplete>
                    </Grid>
                    <Grid item container xs={12} md={6} spacing={0.5}>
                      <Grid
                        item
                        xs={12}
                        sm={hasOtherPersonData(values) ? 6 : 12}
                      >
                        <TextField
                          value={values.clientName}
                          fullWidth
                          variant="outlined"
                          color="success"
                          type="text"
                          label={t("client")}
                          name="client"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="datos de otra persona"
                                  edge="end"
                                  onClick={() =>
                                    handleOpenOtherPersonDialog(values)
                                  }
                                  style={{ marginRight: 4 }}
                                >
                                  <PersonIcon />
                                </IconButton>
                                <Checkbox
                                  style={{ marginRight: -15 }}
                                  checked={activePhone}
                                  onChange={handleActivePhone}
                                  color="success"
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      {hasOtherPersonData(values) && (
                        <>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              value={values.otherPersonName ?? ""}
                              fullWidth
                              variant="outlined"
                              color="success"
                              type="text"
                              label="PARA:"
                              name="otherPersonName"
                              InputProps={{ readOnly: true }}
                              inputProps={{
                                style: { textTransform: "uppercase" },
                              }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              value={values.otherPersonPhone ?? ""}
                              fullWidth
                              variant="outlined"
                              color="success"
                              type="tel"
                              label="Tel:"
                              name="otherPersonPhone"
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={3} md={3}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack spacing={2}>
                              <MobileDatePicker
                                label={t("date")}
                                inputFormat="DD/MM/YYYY"
                                name="date"
                                value={dateformatter}
                                onChange={(newValue) => {
                                  setDateFormat(newValue);
                                  values.date = newValue;
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} />
                                )}
                              />
                            </Stack>
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={6} sm={3} md={3}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack spacing={3}>
                              <MobileTimePicker
                                label={t("time")}
                                value={timeFormatter}
                                name="time"
                                onChange={(newValue) => {
                                  setTimeFormat(newValue);
                                  values.time = newValue;
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            edge="end"
                                            aria-label="fecha y hora final"
                                            onMouseDown={(event) => {
                                              event.preventDefault();
                                            }}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handleOpenEndDateTimeDialog();
                                            }}
                                          >
                                            <AccessTimeIcon fontSize="small" />
                                          </IconButton>
                                          {params.InputProps.endAdornment}
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Stack>
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={6} sm={3} md={3}>
                          <TextField
                            value={values.persons}
                            fullWidth
                            variant="outlined"
                            color="success"
                            type="text"
                            label={t("persons")}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            name="persons"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3} md={3}>
                          <FormControl fullWidth>
                            <InputLabel>{t("Model")}</InputLabel>
                            <Select
                              value={values.vehicle}
                              id="vehicle"
                              name="vehicle"
                              label={t("vehicle")}
                              onChange={handleChange}
                            >
                              <MenuItem value={1}>{t("SEDAN")}</MenuItem>
                              <MenuItem value={2}>{t("SUV")}</MenuItem>
                              <MenuItem value={3}>{t("MINIVAN")}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>

                    {values.booking_type == 3 && (
                      <>
                        <Grid item xs={6} sm={2}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack spacing={2}>
                              <MobileDatePicker
                                label={t("dateEnd")}
                                inputFormat="DD/MM/YYYY"
                                name="dateEnd"
                                value={dateEndFormat}
                                onChange={(newValue) => {
                                  setDateEndFormat(newValue);
                                  setFieldValue("dateEnd", newValue);
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} fullWidth />
                                )}
                              />
                            </Stack>
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack spacing={2}>
                              <MobileTimePicker
                                label={t("timeEnd")}
                                value={timeEndFormatter}
                                name="timeEnd"
                                onChange={(newValue) => {
                                  setTimeEndFormat(newValue);
                                  setFieldValue("timeEnd", newValue);
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} fullWidth />
                                )}
                              />
                            </Stack>
                          </LocalizationProvider>
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} sm={values.booking_type == 3 ? 4 : 6}>
                      <TextField
                        value={values.note}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="text"
                        label={t("notes")}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        name="note"
                      />
                    </Grid>

                    <Grid item xs={12} sm={values.booking_type == 3 ? 4 : 6}>
                      {drivers && (
                        <Autocomplete
                          value={driver}
                          disablePortal
                          id="combo-box-driver"
                          onChange={(e, newValue) => {
                            setDriver(newValue);
                          }}
                          options={drivers}
                          getOptionLabel={(option) =>
                            `${option.name} ${option.lastname} ${option.brand} ${option.model} ${option.bodywork} ${option.plate} ${option.color}`
                          }
                          renderInput={(params) => (
                            <TextField {...params} label={t("driver")} />
                          )}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={3} md={3}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={12} md={12}>
                          <FormControl fullWidth>
                            <InputLabel>{t("Tipo Pago")}</InputLabel>
                            <Select
                              value={values.payment_type}
                              id="payment_type"
                              name="payment_type"
                              label={t("payment")}
                              onChange={handleChange}
                            >
                              <MenuItem value={1}>{t("EFECTIVO")}</MenuItem>
                              <MenuItem value={2}>{t("CREDITO")}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={12} md={12}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel htmlFor="outlined-adornment-password">
                              COMISION
                            </InputLabel>
                            <OutlinedInput
                              value={commissionValue}
                              name="commissionValue"
                              onChange={(e) => {
                                setCommissionValue(e.target.value);
                              }}
                              disabled={commissionOption}
                              id="control-dni"
                              endAdornment={
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                    onClick={(e) => {
                                      setCommissionOption(!commissionOption);
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </InputAdornment>
                              }
                              label="COMISION"
                              aria-describedby="outlined-helper-text"
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={3} md={3}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={12} md={12}>
                          <FormControl fullWidth>
                            <InputLabel>{t("currency")}</InputLabel>
                            <Select
                              value={values.currency}
                              id="currency"
                              name="currency"
                              label={t("currency")}
                              onChange={handleChange}
                            >
                              <MenuItem value={1}>{t("PEN")}</MenuItem>
                              <MenuItem value={2}>{t("USD")}</MenuItem>
                              <MenuItem value={3}>{t("EUR")}</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={3} sm={6} md={6}>
                          <TextField
                            value={discount}
                            name="discount"
                            fullWidth
                            variant="outlined"
                            color="success"
                            type="number"
                            label="DESCUENTO"
                            onChange={(e) => setDiscount(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={3} sm={6} md={6}>
                          <TextField
                            value={advance}
                            name="advance"
                            fullWidth
                            variant="outlined"
                            color="success"
                            type="number"
                            label="ADELANTO"
                            onChange={(e) => setAdvance(e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <Checkbox
                                  style={{ marginRight: -15 }}
                                  checked={otherPay}
                                  onChange={handleOtherPay}
                                  color="success"
                                />
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={3} md={3}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={12} md={12}>
                          <TextField
                            value={values.tax}
                            fullWidth
                            variant="outlined"
                            color="success"
                            type="number"
                            label={t("IMPUESTO")}
                            onChange={handleChange}
                            name="tax"
                          />
                        </Grid>
                        <Grid item xs={6} sm={12} md={12}>
                          <TextField
                            value={values.toll}
                            fullWidth
                            variant="outlined"
                            color="success"
                            type="number"
                            label={t("PEAJE")}
                            onChange={handleChange}
                            name="toll"
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={3} md={3}>
                      <Grid container spacing={1}>
                        <Grid item xs={6} sm={12} md={12}>
                          <TextField
                            value={values.price}
                            fullWidth
                            variant="outlined"
                            color="success"
                            type="number"
                            label={t("P CONDUCTOR")}
                            onChange={handleChange}
                            name="price"
                          />
                        </Grid>
                        <Grid item xs={6} sm={12} md={12}>
                          <TextField
                            value={values.total}
                            fullWidth
                            variant="outlined"
                            color="success"
                            type="number"
                            label={t("TOTAL")}
                            onChange={handleChange}
                            name="total"
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={12} md={12}>
                      <div>
                        {listPayments.map((pay, idx) => {
                          return (
                            <Fragment key={idx}>
                              <span style={{ fontWeight: "bold" }}>
                                {pay.created_by}
                              </span>
                              {", "}
                              <span style={{ fontWeight: "bold" }}>Pago: </span>
                              {pay.amount} {pay.currency},{" "}
                              <span style={{ fontWeight: "bold" }}>
                                Fecha:{" "}
                              </span>
                              {pay.operation_date}
                              {". "}
                            </Fragment>
                          );
                        })}
                      </div>
                    </Grid>

                    <Grid item xs={6} sm={6} md={6}>
                      <TextField
                        value={noteAdmin}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="text"
                        label="Notas del Admin"
                        onChange={(e) => setNoteAdmin(e.target.value)}
                        name="noteAdmin"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="booking return"
                                edge="end"
                                disabled={isLoading}
                                onClick={() => handleOpenCopyDialog("return")}
                                sx={{ mr: 0.5 }}
                              >
                                <SwapHorizIcon />
                              </IconButton>
                              <IconButton
                                aria-label="booking duplicate"
                                edge="end"
                                disabled={isLoading}
                                onClick={() => handleOpenCopyDialog("duplicate")}
                                sx={{ mr: 0.5 }}
                              >
                                <FileCopyIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3} md={3}>
                      <TextField
                        value={datePay}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="text"
                        label="Fecha de Pago"
                        disabled
                        name="datePay"
                        InputProps={{
                          endAdornment: <p>{values?.openpay?.createBy}</p>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3} md={3}>
                      <Button
                        fullWidth
                        type="submit"
                        color="secondary"
                        variant="contained"
                        sx={{ height: "50px" }}
                        disabled={isLoading}
                      >
                        {"GUARDAR"}
                      </Button>
                    </Grid>
                  </Grid>
                  <SnackBar
                    stateSnk={stateSnk}
                    vertical={vertical}
                    horizontal={horizontal}
                    duration={4000}
                    handleClose={handleSnkClose}
                    severity={severity}
                    messageSnk={messageSnk}
                  />
                </Box>
                <Dialog
                  open={Boolean(copyDialogType)}
                  onClose={isLoading ? undefined : handleCloseCopyDialog}
                  fullWidth
                  maxWidth="xs"
                >
                  <DialogTitle>
                    {copyDialogType === "return"
                      ? "¿Deseas crear el pedido de retorno?"
                      : "¿Deseas duplicar este pedido?"}
                  </DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                      <Typography variant="body2">
                        {copyDialogType === "return"
                          ? "Confirma o cambia la fecha y hora del retorno."
                          : "Confirma o cambia la fecha y hora del nuevo pedido."}
                      </Typography>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileDatePicker
                          label={t("date")}
                          inputFormat="DD/MM/YYYY"
                          value={copyDate}
                          onChange={setCopyDate}
                          renderInput={(params) => (
                            <TextField {...params} fullWidth />
                          )}
                        />
                        <MobileTimePicker
                          label={t("time")}
                          value={copyTime}
                          onChange={setCopyTime}
                          renderInput={(params) => (
                            <TextField {...params} fullWidth />
                          )}
                        />
                      </LocalizationProvider>
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      type="button"
                      variant="contained"
                      color="error"
                      onClick={handleCloseCopyDialog}
                      disabled={isLoading}
                    >
                      No
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="success"
                      onClick={handleAcceptCopyDialog}
                      disabled={isLoading}
                    >
                      {copyDialogType === "return"
                        ? "Sí, Crear Retorno"
                        : "Sí, Duplicar Pedido"}
                    </Button>
                  </DialogActions>
                </Dialog>
                <Dialog
                  open={otherPersonDialogOpen}
                  onClose={handleCloseOtherPersonDialog}
                  fullWidth
                  maxWidth="sm"
                >
                  <DialogTitle>Datos de otra persona</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                      <TextField
                        value={otherPersonForm.otherPersonName}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="text"
                        label="Nombre"
                        name="otherPersonName"
                        onChange={handleOtherPersonChange}
                        inputProps={{
                          style: { textTransform: "uppercase" },
                        }}
                      />
                      <TextField
                        value={otherPersonForm.otherPersonPhone}
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="tel"
                        label="Telefono"
                        name="otherPersonPhone"
                        onChange={handleOtherPersonChange}
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      type="button"
                      variant="contained"
                      color="error"
                      onClick={handleCloseOtherPersonDialog}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleClearOtherPerson(setFieldValue)}
                    >
                      Limpiar
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="secondary"
                      onClick={() => handleAcceptOtherPerson(setFieldValue)}
                    >
                      Aceptar
                    </Button>
                  </DialogActions>
                </Dialog>
                <EndDateTimeDialog
                  open={endDateTimeDialogOpen}
                  onClose={handleCloseEndDateTimeDialog}
                  dateEnd={dateEndFormat}
                  timeEnd={timeEndFormatter}
                  dateLabel={t("dateEnd")}
                  timeLabel={t("timeEnd")}
                  onDateEndChange={(newValue) => {
                    setDateEndFormat(newValue);
                    setFieldValue("dateEnd", newValue);
                  }}
                  onTimeEndChange={(newValue) => {
                    setTimeEndFormat(newValue);
                    setFieldValue("timeEnd", newValue);
                  }}
                  onClear={handleClearEndDateTimeDialog}
                  onAccept={handleAcceptEndDateTimeDialog}
                />
                <Dialog
                  open={openSubRoutesModal}
                  onClose={handleSaveSubRoutesModal}
                  fullWidth
                  maxWidth="xs"
                  PaperProps={{
                    sx: {
                      height: "80vh",
                      maxHeight: "90vh",
                    },
                  }}
                >
                  <DialogTitle>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                    >
                      <Typography variant="h6" sx={{ flexShrink: 0 }}>
                        Paradas
                      </Typography>
                      <PlacesAutocomplete
                        value={subRouteSearch}
                        onChange={setSubRouteSearch}
                        onSelect={handleAddSubRoute}
                        searchOptions={{
                          componentRestrictions: { country: "pe" },
                        }}
                      >
                        {({
                          getInputProps,
                          suggestions,
                          getSuggestionItemProps,
                          loading,
                        }) => (
                          <Box sx={{ flex: 1, position: "relative" }}>
                            <TextField
                              fullWidth
                              size="small"
                              label={t("Destino")}
                              {...getInputProps({
                                className: "location-search-input",
                              })}
                            />
                            <Box
                              className="autocomplete-dropdown-container suggestionsDestination"
                              sx={{ width: "100%" }}
                            >
                              {loading && <div>Loading...</div>}
                              {suggestions.map((suggestion) => {
                                const className = suggestion.active
                                  ? "suggestion-item--active"
                                  : "suggestion-item";
                                const style = suggestion.active
                                  ? suggestionBackgroundColor(
                                      colors.primary[500],
                                    )
                                  : suggestionBackgroundColor(
                                      colors.primary[300],
                                    );

                                return (
                                  <div
                                    key={suggestion.description}
                                    {...getSuggestionItemProps(suggestion, {
                                      className,
                                      style,
                                    })}
                                  >
                                    <span>{suggestion.description}</span>
                                  </div>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </PlacesAutocomplete>
                    </Stack>
                  </DialogTitle>
                  <DialogContent>
                    <Stack spacing={1} sx={{ pt: 1 }}>
                      <DragDropProvider onDragEnd={handleSubRoutesDragEnd}>
                        <Stack spacing={1}>
                          {subRoutesModalList.map((subRoute, index) => (
                            <SubRouteSortableItem
                              key={subRoute.sortableId}
                              subRoute={subRoute}
                              index={index}
                              onDelete={handleDeleteSubRoute}
                            />
                          ))}
                        </Stack>
                      </DragDropProvider>
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      type="button"
                      variant="contained"
                      color="error"
                      onClick={handleSaveSubRoutesModal}
                    >
                      {t("Cerrar")}
                    </Button>
                  </DialogActions>
                </Dialog>
              </form>
            );
          }}
        </Formik>
      )}
    </div>
  );
};

export default FormBookingLater;
